import type {ReactNode, ComponentType, ComponentProps} from 'react';
import type {Props as ComboBoxProps} from '@pb/kit-component-combo-box';

import type {PaginationParams, PaginationRangeType} from '../typedef';
import type {SwitcherProps} from './switcher/typedef';

import React, {PureComponent} from 'react';
import {withCssModule} from '@pb/kit-util-with-css-module';
import isEqual from 'lodash.isequal';

import {PaginationRangeTypes} from '../typedef';
import {Switcher} from './switcher/switcher.component';
import {Pagination} from '../pagination/pagination.component';
import {SIZES, ALL_RESULTS} from './constants';
import {FIRST_PAGE} from '../pagination/constants';
import styles from './pagination-stripe.component.module.styl';


const classNames = withCssModule(styles);

export type PaginationStripeStateParams = {
    totalCount: number,
    currentPage: number,
    pageSize: number,
    sizes: Array<number>,
};

type PaginationStripeStateType = (props: PaginationStripeProps | PaginationStripeStateParams) => PaginationParams;

const getPaginationStripeState: PaginationStripeStateType = props => {
    const {totalCount, currentPage, sizes = SIZES} = props;
    const currentPageSize = props.pageSize === undefined ? sizes[0] : props.pageSize;

    let pageSize;
    let totalPages;
    if (currentPageSize === ALL_RESULTS) {
        pageSize = ALL_RESULTS;
        totalPages = 1;
    } else {
        pageSize = totalCount! < currentPageSize
            ? sizes.find((item: number) => totalCount! <= item) || currentPageSize
            : currentPageSize;
        totalPages = Math.ceil(totalCount! / pageSize);
    }

    return {
        totalPages,
        totalCount,
        currentPage,
        pageSize,
    } as PaginationParams;
};

export type OnPaginationChange = {prevPage?: number, currentPage: number, entryPoint?: string, pageSize: number};
export type PaginationChangeHandler = (data: OnPaginationChange) => void | Promise<void>;

type NativeNavProps = Omit<ComponentProps<'nav'>, 'onChange'>;

export interface PaginationStripeProps extends NativeNavProps {
    /**
     * Function check ability to change pagination values
     */
    canChange?: () => boolean,
    /**
     * Props for ComboBox
     */
    comboBoxProps?: Partial<ComboBoxProps<number>>,
    /**
     * Current active page
     */
    currentPage?: number,
    /**
     * Disabled Pagination
     */
    disabled?: boolean,
    /**
     * Show Go To Page
     */
    goToPage?: boolean,
    /**
     * Defines buttons size
     */
    size?: 'small' | 'normal',
    /**
     * The width of parent container where pagination uses (number of pixels)
     */
    maxWidth?: number,
    /**
     * ClassName for pagination stripe state
     * @deprecated It will be removed in 6 months 09-20-2025. use className instead
     */
    mix?: string,
    /**
     * @deprecated It will be removed in 6 months 09-15-2025. Callback on pagination change. Use onPaginationChange to handle onChange
     */
    onChange?: (currentPage: number, pageSize: number) => void,
    /**
     * Called after change pagination size
     */
    onChangePaginationSize?: (currentPage: number, pageSize: number) => void,
    /**
     * Callback on pagination change
     */
    onPaginationChange?: PaginationChangeHandler,
    /**
     * Number of items on page
     */
    pageSize?: number,
    /**
     * Pagination range Type
     */
    range?: PaginationRangeType,
    /**
     * Allows to change amount of elements per page
     */
    sizes?: Array<number>,
    /**
     * SizeSwitcher Position
     */
    sizeSwitcherPosition?: 'LEFT' | 'RIGHT',
    /**
     * SizeSwitcher Type
     */
    sizeSwitcherType?: 'SHOW_RESULTS' | 'ONLY_PAGE_SIZE' | 'NONE',
    /**
     * Show Last Page
     */
    showLastPage?: boolean,
    /**
     * Do not render page size switcher
     */
    showSizeSwitcher?: boolean,
    /**
     * Label for switcher
     * */
    switcherLabel?: ReactNode,
    /**
     * Total count of items
     */
    totalCount?: number,
    /**
     * Update values in state only when props are changed
     */
    usePropsOnly?: boolean,
}

type State = PaginationParams;

/**
 * @description The `PaginationStripe` component is a higher-level component that includes the `Pagination` component
 * and additional features.
 *
 * ## Features
 * - Includes switcher to control the number of items per page.
 *
 * ## Accessibility
 * - The root element of `PaginationStripe` should have an `aria-label` attribute for accessibility.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/components-pagination--pagination-default}
 **/

export const PaginationStripe = class PaginationStripe extends PureComponent<PaginationStripeProps, State> {
    static defaultProps = {
        canChange: () => true,
        goToPage: false,
        onChange: () => undefined,
        onChangePaginationSize: () => undefined,
        onPaginationChange: () => undefined,
        showLastPage: true,
        showSizeSwitcher: true,
        sizeSwitcherPosition: 'RIGHT',
        sizeSwitcherType: 'SHOW_RESULTS',
        size: 'normal',
        sizes: SIZES,
    };

    constructor(props: PaginationStripeProps) {
        super(props);
        this.state = getPaginationStripeState(props);
    }

    componentDidUpdate(prevProps: PaginationStripeProps) {
        if (!isEqual(this.props, prevProps)) {
            this.setState(getPaginationStripeState(this.props));
        }
    }

    onChange = ({prevPage, currentPage, entryPoint, pageSize: currentPageSize}: OnPaginationChange) => {
        const pageSize = ALL_RESULTS === currentPageSize ? this.props.totalCount : currentPageSize;
        if (this.props.onChange) {
            this.props.onChange.apply(this, [currentPage, pageSize!]);
        }
        if (this.props.onPaginationChange && pageSize !== undefined) {
            this.props.onPaginationChange.apply(this, [{prevPage, currentPage, entryPoint, pageSize}]);
        }
    };

    onChangeCurrent = (page: number, entryPoint: string) => {
        const {currentPage: prevPage, pageSize} = this.state;
        if (page !== prevPage) {
            const currentPage = (this.props.canChange && this.props.canChange()) ? page : prevPage;
            const nextChange = {prevPage, currentPage, pageSize, entryPoint};
            if (!this.props.usePropsOnly) {
                this.setState({
                    currentPage,
                }, () => this.onChange(nextChange));
            } else {
                this.onChange(nextChange);
            }
        }
    };

    onChangePages = (pageSize: number) => {
        const {currentPage: prevPage} = this.state;
        const {totalCount, sizes, usePropsOnly} = this.props;

        const nextState = getPaginationStripeState({
            pageSize,
            currentPage: 1,
            totalCount,
            sizes,
        });
        const nextChange = {
            prevPage,
            currentPage: nextState.currentPage,
            pageSize: nextState.pageSize,
        };

        if (!usePropsOnly) {
            this.setState(nextState, () => {
                this.onChange(nextChange);
            });
        } else {
            this.onChange(nextChange);
        }
        if (this.props.onChangePaginationSize) {
            this.props.onChangePaginationSize(prevPage, pageSize);
        }
    };

    render() {
        const {
            canChange,
            comboBoxProps,
            disabled,
            goToPage = false,
            mix = '',
            maxWidth,
            size = 'normal',
            range = PaginationRangeTypes.NORMAL,
            showLastPage = true,
            showSizeSwitcher = true,
            sizeSwitcherPosition = 'RIGHT',
            sizeSwitcherType = 'SHOW_RESULTS',
            sizes = SIZES,
            switcherLabel = 'Show',
            usePropsOnly,

            onChange, pageSize, totalCount, currentPage, onPaginationChange, onChangePaginationSize,
            ...restProps
        } = this.props;

        const MIN_ELEMENTS_COUNT = sizes[0];
        if (this.state.totalCount <= MIN_ELEMENTS_COUNT) {
            return null;
        }

        const switcherProps: SwitcherProps = {
            comboBoxProps,
            disabled: disabled || canChange && !canChange(),
            showSizeSwitcher,
            sizeSwitcherType,
            sizes,
            state: this.state,
            switcherLabel,
            totalCount: this.state.totalCount,
            onChangePages: this.onChangePages,
        };

        const SwitcherType = <Switcher {...switcherProps}/>;

        const navProps = {
            ...restProps,
            'aria-label': restProps['aria-label'] || 'Pagination',
            'className': classNames(
                'pagination-stripe',
                restProps.className,
                mix, {
                    'pagination-stripe_small-container': maxWidth,
                    'pagination-stripe_small': size === 'small',
                    'pagination-stripe_no-pagination': this.state.totalPages <= FIRST_PAGE,
                }),
        };

        return (
            <nav {...navProps}>
                {sizeSwitcherPosition === 'LEFT' ? SwitcherType : null}
                <Pagination
                    currentPage={this.state.currentPage}
                    disabled={disabled}
                    goToPage={goToPage}
                    size={size}
                    maxWidth={maxWidth}
                    onChange={this.onChangeCurrent}
                    range={range}
                    showLastPage={showLastPage}
                    totalPages={this.state.totalPages}
                    usePropsOnly={usePropsOnly}
                />
                {sizeSwitcherPosition === 'RIGHT' ? SwitcherType : null}
            </nav>
        );
    }
} as ComponentType<PaginationStripeProps>;
